<template>
  <div class="inscription-list-table disable-prefix-input-color">
    <el-table
      :data="rows"
      @sort-change="doChangeSort"
      ref="table"
      row-key="id"
      v-loading="loading"
      @row-click="onRowClick"
    >
      <!-- Name -->
      <el-table-column
        :label="fields.firstName.label"
        :prop="fields.firstName.name"
        min-width="100"
      >
        <template slot-scope="scope">
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            effect="dark"
            :content="scope.row.id"
            placement="bottom"
          >
            <span
              class="list__avatar__name"
              v-text="`${presenter(scope.row, 'firstName')} ${presenter(scope.row, 'lastName')}`"
            />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Program -->
      <el-table-column :label="fields.program.label" :prop="fields.program.name" min-width="100">
        <template slot-scope="scope">
          <span
            v-if="scope.row.program &&  scope.row.program.type && scope.row.program.name"
            v-text="scope.row.program.type.toUpperCase() +' - '+ `${presenter(scope.row.program, 'name')}`"
          />
        </template>
      </el-table-column>

      <!-- Gender -->
      <el-table-column
        :label="fields.gender.label"
        :prop="fields.gender.name"
        sortable="custom"
        min-width="60"
      >
        <template slot-scope="scope">
          <span v-text="`${presenter(scope.row, 'gender').charAt(0)}`" />
        </template>
      </el-table-column>

      <!-- Birthdate -->
      <el-table-column :label="fields.birthdate.label" :prop="fields.birthdate.name" min-width="80">
        <template slot-scope="scope">{{ presenter(scope.row, 'birthdate') }}</template>
      </el-table-column>

      <!-- Subprogram -->
      <el-table-column
        :label="fields.subprogram.label"
        :prop="fields.subprogram.name"
        min-width="80"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.subprogram && scope.row.subprogram.name"
            v-text="scope.row.subprogram.name"
          />
        </template>
      </el-table-column>

      <!-- Created At -->
      <!-- <el-table-column
        v-if="hasPermissionToAuditLogs"
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column>-->

      <!-- Status -->
      <el-table-column 
        :label="fields.status.label"
        :prop="fields.status.name"
        min-width="120" 
        width="200" 
        align="center"
        class="inscription-list-table__status"
      >
        <template slot-scope="scope">
          <inscription-change-status
            :inscriptionData="scope.row"
            :subpropertyId="subpropertyId"
            :onFinishSubmitFn="doOnFinishChangeStatusSubmit"
            :doNavigation="true"
          ></inscription-change-status>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { InscriptionModel } from '@modules/inscription/inscription-model';
import { mapGetters, mapActions } from 'vuex';
import { InscriptionPermissions } from '@modules/inscription/inscription-permissions';
import { i18n } from '@shared/i18n';
import InscriptionChangeStatus from '@modules/inscription/components/inscription-change-status.vue';

export default {
  name: 'app-inscription-list-table',

  props: {
    manage: {
      type: Object,
      required: true,
    },
 
  },

  computed: {
    ...mapGetters({
      rows: 'inscription/list/rows',
      count: 'inscription/list/count',
      loading: 'inscription/list/loading',
      pagination: 'inscription/list/pagination',
      filter: 'inscription/list/filter',
      currentUser: 'auth/currentUser',
      destroyLoading: 'inscription/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      dateFormat: 'auth/manage/dateFormat',
    }),

    subpropertyId() {
      const {
        subproperty: { id },
      } = this.manage;
      if (id) return id;
      else return null;
    },

    propertyName() {
      return '';
    },

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new InscriptionPermissions(this.currentUser)
        .edit;
    },

    hasPermissionToDestroy() {
      return new InscriptionPermissions(this.currentUser)
        .destroy;
    },

    fields() {
      const { propertyName, dateFormat } = this;
      return InscriptionModel.fields({
        propertyName,
        dateFormat,
      });
    },

    tabName() {
      return this.$route.params.tabName;
    },

    subtabName() {
      return this.$route.params.subtabName;
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'inscription/list/doChangeSort',
      doChangePaginationCurrentPage:
        'inscription/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'inscription/list/doChangePaginationPageSize',
      doFetch: 'inscription/list/doFetch',
      doDestroy: 'inscription/destroy/doDestroy',
      doUpdate: 'inscription/form/doUpdate',
    }),

    presenter(row, fieldName) {
      const { propertyName, dateFormat } = this;
      return InscriptionModel.presenter(
        row,
        fieldName,
        propertyName,
        dateFormat,
      );
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },

    i18n(code) {
      return i18n(code);
    },

    onRowClick(row, column) {
      const isClickOnColumnStatus =
        column.label === this.fields.status.label;
      if (isClickOnColumnStatus) return;

      this.$router.push({
        name: 'subpropertyPeopleInscriptionDetail',
        params: {
          id: row.id,
          subpropertyId: this.subpropertyId,
          tabName: this.tabName,
          subtabName: this.subtabName,
        },
      });
    },

    doOnFinishChangeStatusSubmit() {
      return this.doFetch({
        manage: this.manage,
        filter: this.filter,
      });
    },
  },

  components: {
    [InscriptionChangeStatus.name]: InscriptionChangeStatus,
  },
};
</script>

<style>
.inscription-list-table .el-table:not(.el-table--simple) td:last-of-type .cell {
  padding: 0 16px 0 16px;

}
</style>